import { useState } from 'react';
import { StyleSheet, Text, TextInput, View, type TextInputProps } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, control, fontFamily, radii, space, spacing } from '~/ui/tokens';

interface InputProps extends Pick<TextInputProps, 'value' | 'onChangeText' | 'placeholder' | 'editable' | 'keyboardType'> {
  label?: string;
  error?: string;
  leftIcon?: keyof typeof Ionicons.glyphMap;
}

export function Input({ label, error, leftIcon, editable = true, ...inputProps }: InputProps) {
  const [focused, setFocused] = useState(false);

  return (
    <View>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      <View
        style={[
          styles.field,
          focused && styles.fieldFocused,
          !!error && styles.fieldError,
          !editable && styles.fieldDisabled,
        ]}
      >
        {leftIcon ? <Ionicons name={leftIcon} size={18} color={colors.inkFaint} style={styles.icon} /> : null}
        <TextInput
          {...inputProps}
          editable={editable}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholderTextColor={colors.inkFaint}
          style={styles.textInput}
        />
      </View>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  label: {
    fontFamily: fontFamily.medium,
    ...control.inputLabel,
    color: colors.inkSoft,
    marginBottom: space.related,
  },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    paddingHorizontal: spacing(1.75),
    height: 48,
    backgroundColor: colors.card,
  },
  fieldFocused: {
    borderColor: colors.brand,
  },
  fieldError: {
    borderColor: colors.pink,
  },
  fieldDisabled: {
    backgroundColor: colors.lineSoft,
  },
  icon: {
    marginRight: spacing(1),
  },
  textInput: {
    flex: 1,
    fontFamily: fontFamily.regular,
    ...control.inputValue,
    color: colors.ink,
    height: '100%',
    // react-native-web-only property (removes the default browser focus
    // ring in favor of the custom border above); RN's style types don't
    // know about it, hence the cast.
    ...({ outlineStyle: 'none' } as object),
  },
  errorText: {
    fontFamily: fontFamily.regular,
    ...control.inputError,
    color: colors.pink,
    marginTop: space.micro,
  },
});
